import {
  CANDIDATE_DRAWERS,
  CANDIDATE_PROFILE_CONFIG,
  CANDIDATE_TAB_IDS
} from 'features/inventory-profiles/constants';
import { buildTabDataTableProps, getCommonTabs } from 'features/inventory-profiles/utils';
import strings from 'strings';
import { ReferenceCheckTabLayout } from 'UI/components/organisms/inventoryProfiles';
import CandidateOverview from 'UI/components/organisms/inventoryProfiles/CandidateOverview';
import JobOrdersTab from 'UI/components/organisms/inventoryProfiles/JobOrdersTab';
import { MatchMakingTab } from 'UI/components/organisms/inventoryProfiles/MatchMakingTab';
import PlacementsTab from 'UI/components/organisms/inventoryProfiles/PlacementsTab';
import SendoutsTab from 'UI/components/organisms/inventoryProfiles/SendoutsTab';
import { TasksTab } from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { ResumeViewer } from 'UI/components/organisms/inventoryProfiles/viewers';
import { EntityType } from 'UI/constants/entityTypes';

import { CANDIDATE_TABS_CONFIG } from './CandidateProfileV2.constants';

const COPIES = strings.inventoryProfiles;

/**
 * Tabs of the Candidate profile, each one with its own visibility.
 * @param {Object} params
 * @param {Object} params.candidate Candidate with `referenceReleases` from useCandidateProfile
 * @param {string} params.candidateName `formattedCandidate.header.title`
 * @param {Object} [params.dataSheet] Candidate data sheet
 * @param {string} params.dataSheetResponseStatus RequestStatus of the data sheet request
 * @param {boolean} params.isLoading The profile is loading
 * @param {Object} params.permissions Result of useCandidatePermissions
 * @param {(name: string) => () => void} params.openDrawer From useProfileDrawer
 * @param {(name: string) => (item: { id: number|string }) => void} params.openItemDrawer From useProfileDrawer
 * @param {() => void} params.onWriteUpClick `openDataSheet` of useCandidateActions
 * @param {() => void} params.refresh Reloads the profile silently
 * @param {(tabId: string) => void} params.refreshTab From useTabRefresh
 * @param {{ id: string, tabProps: Object }} params.searchProjectsTab `searchProjectTabPropsDefinition` of useSearchProjectInProfiles
 * @returns {Array<{ id: string, label: string, content: React.ReactNode, visible: boolean }>}
 */
export const buildCandidateTabs = ({
  candidate,
  candidateName,
  dataSheet,
  dataSheetResponseStatus,
  isLoading,
  permissions,
  openDrawer,
  openItemDrawer,
  onWriteUpClick,
  refresh,
  refreshTab,
  searchProjectsTab
}) => {
  const profileEndpoint = `${CANDIDATE_PROFILE_CONFIG.endpoint}/${candidate.id}`;
  const {
    [CANDIDATE_TAB_IDS.MatchMaking]: matchMaking,
    [CANDIDATE_TAB_IDS.JobOrders]: jobOrders,
    [CANDIDATE_TAB_IDS.Sendouts]: sendouts,
    [CANDIDATE_TAB_IDS.Placements]: placements,
    [CANDIDATE_TAB_IDS.ReferenceChecks]: referenceChecks,
    [CANDIDATE_TAB_IDS.ActionPlan]: actionPlan
  } = CANDIDATE_TABS_CONFIG;

  return [
    {
      id: CANDIDATE_TAB_IDS.Overview,
      label: COPIES.common.tabs.overview,
      visible: true,
      content: (
        <CandidateOverview
          candidateDataSheet={dataSheet}
          candidateDataSheetStatus={dataSheetResponseStatus}
          isProfileLoading={isLoading}
          onEditClick={openDrawer(CANDIDATE_DRAWERS.Edit)}
          onWriteUpClick={onWriteUpClick}
          profile={candidate}
        />
      )
    },
    {
      id: CANDIDATE_TAB_IDS.Resume,
      label: COPIES.common.tabs.resume,
      visible: true,
      content: <ResumeViewer candidateId={candidate.id} />
    },
    {
      id: CANDIDATE_TAB_IDS.MatchMaking,
      label: COPIES.common.tabs.matchMaking,
      visible: true,
      content: (
        <MatchMakingTab
          availableEntityTypesToMatch={matchMaking.availableEntityTypesToMatch}
          DataTableProps={matchMaking.DataTableProps}
          profileId={candidate.id}
        />
      )
    },
    {
      id: CANDIDATE_TAB_IDS.JobOrders,
      label: COPIES.common.tabs.jobOrders,
      visible: true,
      content: (
        <JobOrdersTab
          DataTableProps={buildTabDataTableProps({ profileEndpoint, tabConfig: jobOrders })}
          endpoint={profileEndpoint}
          initialColumns={jobOrders.initialColumns}
          itemName={candidateName}
          mode={EntityType.Candidate}
          profileId={candidate.id}
          tablePreferencesKey={jobOrders.tablePreferencesKey}
        />
      )
    },
    {
      id: CANDIDATE_TAB_IDS.Sendouts,
      label: COPIES.common.tabs.sendouts,
      visible: true,
      content: (
        <SendoutsTab
          DataTableProps={buildTabDataTableProps({ profileEndpoint, tabConfig: sendouts })}
          extraRenderers={sendouts.extraRenderers}
          initialColumns={sendouts.initialColumns}
          onSendoutClick={openItemDrawer(CANDIDATE_DRAWERS.Sendout)}
          profile={candidate}
          profileModule={EntityType.Candidate}
          tablePreferencesKey={sendouts.tablePreferencesKey}
        />
      )
    },
    {
      id: CANDIDATE_TAB_IDS.Placements,
      label: COPIES.common.tabs.placements,
      visible: permissions.canSeePlacements,
      content: (
        <PlacementsTab
          DataTableProps={buildTabDataTableProps({ profileEndpoint, tabConfig: placements })}
          initialColumns={placements.initialColumns}
          isLoading={isLoading}
          onTriggerTabRefresh={refreshTab}
          profileId={candidate.id}
          profileModule={EntityType.Candidate}
          search={false}
          tablePreferencesKey={placements.tablePreferencesKey}
        />
      )
    },
    {
      id: CANDIDATE_TAB_IDS.ReferenceChecks,
      label: COPIES.common.tabs.referenceChecks,
      visible: true,
      content: (
        <ReferenceCheckTabLayout
          additionalRecruiters={candidate.additionalRecruiters}
          candidateId={candidate.id}
          candidateName={candidateName}
          DataTableProps={referenceChecks.DataTableProps}
          initialColumns={referenceChecks.initialColumns}
          recruiter={candidate.recruiter}
          tablePreferencesKey={referenceChecks.tablePreferencesKey}
        />
      )
    },
    {
      id: CANDIDATE_TAB_IDS.ActionPlan,
      label: COPIES.sections.tabs.actionPlan.tabLabel,
      visible: permissions.canUseDailyPlan,
      content: (
        <TasksTab
          DataTableProps={actionPlan.DataTableProps}
          entityType={actionPlan.entityType}
          entityTypeId={actionPlan.entityTypeId}
          extraRenderers={actionPlan.extraRenderers}
          initialColumns={actionPlan.initialColumns}
          itemName={candidateName}
          profileId={candidate.id}
          tablePreferencesKey={actionPlan.tablePreferencesKey}
        />
      )
    },
    ...getCommonTabs({
      profileId: candidate.id,
      entity: CANDIDATE_PROFILE_CONFIG,
      itemName: candidateName,
      onActivityComplete: refresh,
      searchProjectsTab
    })
  ];
};
